/**
 * Weather wording and readouts for the HUD (pure; unit-tested). The view carries the public
 * weather: cells, sea-level temperature, per-hex condition bits and the MET forecast
 * (docs/Weather.md); these helpers turn it into glyphs, words and the rules a player feels.
 */
import {
  CLOUD_MIN,
  type ForecastCondition,
  type GameMap,
  type HexGrid,
  type HexId,
  hexTemperature,
  isNight,
  isWater,
  type PlayerView,
  precipitationAt,
  type Season,
  W_FOG,
  W_FROZEN,
  W_MUD,
  W_RAIN,
  W_SNOW,
  W_SNOWING,
  W_STORM,
  W_WET,
  WEATHER_ATTACK,
  WEATHER_MOVE,
  WEATHER_SIGHT,
  type WeatherForecast,
  weatherCoords,
} from '@friction/engine';
import { getLocale, type Locale, tr } from '../i18n';

export type SkyIcon = 'sun' | 'moon' | 'cloud' | 'rain' | 'storm' | 'snow' | 'fog';
export type SkyKind = 'clear' | 'fair' | 'overcast' | 'rain' | 'storm' | 'snow' | 'blizzard' | 'fog';
export type GroundKind = 'mud' | 'snow' | 'frozen' | 'wet';

export const seasonLabel = (season: Season, locale: Locale = getLocale()): string =>
  tr(locale)(`season.${season}`);

/** One line per season for the setup screens: what it does to a campaign. */
export const seasonBlurb = (season: Season | 'random', locale: Locale = getLocale()): string =>
  tr(locale)(`season.blurb.${season}`);

/** Weather at one hex, as the HUD shows it. */
export interface Sky {
  cond: number;
  /** Precipitation 0..1 from the cells. */
  precip: number;
  /** Air temperature at the hex, °C. */
  temp: number;
  icon: SkyIcon;
  kind: SkyKind;
  /** "Rain", "Storm", "Blizzard", "Snow", "Fog", "Overcast", "Fair", "Clear" — in the given language. */
  word: string;
  groundKind: GroundKind | null;
  /** "Mud", "Snow cover", "Frozen", "Wet", or null (firm, dry ground). */
  ground: string | null;
}

export function skyAt(
  view: PlayerView,
  map: GameMap,
  grid: HexGrid,
  hex: HexId,
  locale: Locale = getLocale(),
): Sky {
  const w = view.weather;
  const cond = w.conditions[hex] ?? 0;
  const xy = weatherCoords(grid);
  const precip = precipitationAt(w.cells, xy[hex * 2], xy[hex * 2 + 1]);
  const temp = hexTemperature(w.temperature, map.elevation[hex] ?? 0);
  const night = isNight(view.tick);
  let icon: SkyIcon = night ? 'moon' : 'sun';
  let kind: SkyKind = night ? 'clear' : 'fair';
  if (cond & W_STORM) {
    icon = cond & W_SNOWING ? 'snow' : 'storm';
    kind = cond & W_SNOWING ? 'blizzard' : 'storm';
  } else if (cond & W_RAIN) {
    icon = cond & W_SNOWING ? 'snow' : 'rain';
    kind = cond & W_SNOWING ? 'snow' : 'rain';
  } else if (cond & W_FOG) {
    icon = 'fog';
    kind = 'fog';
  } else if (precip >= CLOUD_MIN) {
    icon = 'cloud';
    kind = 'overcast';
  }
  const groundKind = groundOf(cond);
  const t = tr(locale);
  return {
    cond,
    precip,
    temp,
    icon,
    kind,
    word: t(`sky.${kind}`),
    groundKind,
    ground: groundKind ? t(`ground.${groundKind}`) : null,
  };
}

/** The state of the ground that changes the rules, or null for firm, dry ground. */
export function groundOf(cond: number): GroundKind | null {
  if (cond & W_MUD) return 'mud';
  if (cond & W_SNOW) return 'snow';
  if (cond & W_FROZEN) return 'frozen';
  if (cond & W_WET) return 'wet';
  return null;
}

export function groundWord(cond: number, locale: Locale = getLocale()): string | null {
  const g = groundOf(cond);
  return g ? tr(locale)(`ground.${g}`) : null;
}

/** "5°C" / "5 °C" */
export const formatTemp = (t: number, locale: Locale = getLocale()): string =>
  tr(locale)('time.temp', { t: Math.round(t) === 0 ? 0 : Math.round(t) });

const pct = (m: number): string => `${m > 1 ? '+' : '−'}${Math.round(Math.abs(m - 1) * 100)} %`;

/**
 * What the weather at a hex does to the rules, in short phrases ("marching +20 %", "attacks
 * −10 %", "sight −1") — the tooltip under the hex readout.
 */
export function weatherEffects(
  cond: number,
  terrainIsMarsh: boolean,
  locale: Locale = getLocale(),
): string[] {
  const t = tr(locale);
  const out: string[] = [];
  if (cond & W_STORM)
    out.push(
      t('wx.fx.marching', { pct: pct(WEATHER_MOVE.storm) }),
      t('wx.fx.attacks', { pct: pct(WEATHER_ATTACK.storm) }),
    );
  else if (cond & W_RAIN)
    out.push(
      t('wx.fx.marching', { pct: pct(WEATHER_MOVE.rain) }),
      t('wx.fx.attacks', { pct: pct(WEATHER_ATTACK.rain) }),
    );
  if (cond & W_FOG)
    out.push(
      t('wx.fx.sight', { n: WEATHER_SIGHT.fog }),
      t('wx.fx.hidden'),
      t('wx.fx.attackers', { pct: pct(WEATHER_ATTACK.fog) }),
    );
  else if (cond & W_STORM) out.push(t('wx.fx.sight', { n: WEATHER_SIGHT.storm }), t('wx.fx.noAir'));
  else if (cond & W_RAIN) out.push(t('wx.fx.sight', { n: WEATHER_SIGHT.rain }), t('wx.fx.noAir'));
  if (cond & W_MUD)
    out.push(
      t('wx.fx.mud', { foot: pct(WEATHER_MOVE.mudFoot), motor: pct(WEATHER_MOVE.mudMotor) }),
      t('wx.fx.tankAttacks', { pct: pct(WEATHER_ATTACK.mudArmor) }),
    );
  else if (cond & W_SNOW) out.push(t('wx.fx.snow', { pct: pct(WEATHER_MOVE.snow) }));
  if (cond & W_FROZEN) out.push(t(terrainIsMarsh ? 'wx.fx.frozenMarsh' : 'wx.fx.frozenRivers'));
  return out;
}

/** Compass word for where the wind comes from (the vector is where it blows to; y points south). */
export function windFrom(wind: { x: number; y: number }): string {
  // The wind comes from −(x, y); with north = −y that is (east −x, north +y).
  const deg = (Math.atan2(wind.y, -wind.x) * 180) / Math.PI;
  const names = ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE'];
  return names[Math.round(((deg % 360) + 360) / 45) % 8];
}

export interface Outlook {
  kind: 'clear' | ForecastCondition;
  /** Chance (0..100) of that condition (for 'clear': of staying dry). */
  chance: number;
}

/** A region's outlook at forecast step `step`. */
export function outlookAt(f: WeatherForecast, region: number, step: number): Outlook {
  const i = region * f.leads.length + step;
  const precip = f.precip[i] ?? 0;
  const severe = f.severe[i] ?? 0;
  const snowy = (f.snow[i] ?? 0) >= 50;
  if (severe >= 40) return { kind: snowy ? 'blizzard' : 'storm', chance: severe };
  if (precip >= 40) return { kind: snowy ? 'snow' : 'rain', chance: precip };
  return { kind: 'clear', chance: 100 - precip };
}

export const OUTLOOK_ICON: Record<Outlook['kind'], SkyIcon> = {
  clear: 'sun',
  rain: 'rain',
  storm: 'storm',
  snow: 'snow',
  blizzard: 'snow',
  fog: 'fog',
};

/** Share of land hexes with each ground/sky condition (Met drawer summary). */
export function theatreShares(
  view: PlayerView,
  map: GameMap,
): Record<'rain' | 'mud' | 'snow' | 'frozen' | 'fog', number> {
  let land = 0;
  const n = { rain: 0, mud: 0, snow: 0, frozen: 0, fog: 0 };
  const c = view.weather.conditions;
  for (let h = 0; h < c.length; h++) {
    if (isWater(map.terrain[h])) continue;
    land++;
    if (c[h] & W_RAIN) n.rain++;
    if (c[h] & W_MUD) n.mud++;
    if (c[h] & W_SNOW) n.snow++;
    if (c[h] & W_FROZEN) n.frozen++;
    if (c[h] & W_FOG) n.fog++;
  }
  const k = Math.max(1, land);
  return { rain: n.rain / k, mud: n.mud / k, snow: n.snow / k, frozen: n.frozen / k, fog: n.fog / k };
}
